<template>
  <v-row no-gutters>
    <v-col cols="12" class="px-0">
      <card-title-row
        is-subtitle
        :straight-title="headerTitle"
        class="pb-3"
        :amount="1"
        :is-plural="true"
        :has-icon-column="false"
        :buttons-align="buttonsAlign"
      >
        <template #buttons>
          <slot name="header.buttons" />
        </template>
      </card-title-row>
      <v-list class="py-0 rounded-lg elevation-4" dense v-if="!loading">
        <v-list-item
          v-for="(measurement, idx) in fullListVisible || !wrapping
            ? computedMeasurements
            : computedMeasurements.slice(0, wrappingElementsNumber)"
          :key="idx"
          class="measurement-element hover-list-element"
        >
          <v-list-item-content>
            <v-list-item-title class="text-body-2">
              <v-row no-gutters class="align-center flex-nowrap">
                <v-col cols="7">
                  <v-row no-gutters class="flex-col">
                    <v-col cols="12" class="text-left">
                      <v-tooltip bottom color="font">
                        <template #activator="{ on }">
                          <div class="text-body-2 text-truncate line--16" v-on="on">
                            {{ measurement.verbose_name }}
                          </div>
                        </template>
                        {{ measurement.verbose_name }}
                      </v-tooltip>
                    </v-col>
                    <v-col cols="12" v-if="measurement.measurement_exists_in_system && measurement.formattedDateText">
                      <v-tooltip bottom color="font">
                        <template #activator="{ on }">
                          <italic-subheader
                            class="line--16"
                            v-on="on"
                            :straight-text="measurement.formattedDateText"
                            style="font-size: 10px !important;"
                          >
                          </italic-subheader>
                          <!-- <div class="text-truncate text-caption" v-on="on">
                          </div> -->
                        </template>
                        {{ measurement.formattedDateText }}
                      </v-tooltip>
                    </v-col>
                  </v-row>
                </v-col>
                <v-spacer></v-spacer>
                <template v-if="measurement.measurement_exists_in_system">
                  <template v-for="(button, idx) of measurement.contentButtons">
                    <base-rounded-button
                      v-bind="button"
                      :key="idx"
                      @click="contentButtonHandler(button, measurement)"
                    />
                  </template>
                </template>
                <v-col
                  cols="auto"
                  v-if="measurement.measurement_exists_in_system && (measurement.value || measurement.value == '0')"
                >
                  <v-tooltip bottom color="font">
                    <template #activator="{ on }">
                      <v-chip label color="rgba(0,145,234,1)" small v-on="on" style="height: 20px">
                        <div class="white--text text-truncate">
                          {{ measurement.fixedValue }}
                        </div>
                      </v-chip>
                    </template>
                    {{ measurement.tooltipValue }}
                  </v-tooltip>
                </v-col>
                <v-col cols="auto" style="max-width: 10%" v-if="!measurement.measurement_exists_in_system">
                  <base-rounded-button
                    :tooltip="errorTitle"
                    icon="mdi-exclamation"
                    icon-size="16"
                    :custom-size="20"
                    color="error"
                  />
                </v-col>
                <v-col cols="auto" v-if="measurement.hasDotsMenu">
                  <dots-menu :items="measurement.dotsMenuItems" v-on="getListeners(measurement)" />
                </v-col>
              </v-row>
            </v-list-item-title>
          </v-list-item-content>
        </v-list-item>
        <list-toggler v-if="wrapping && measurements.length > wrappingElementsNumber" v-model="fullListVisible" />
      </v-list>
      <v-row v-else class="pb-2">
        <main-loader />
      </v-row>
    </v-col>
  </v-row>
</template>
<script>
import CardTitleRow from '@/components/CardTitleRow';
import DotsMenu from '@/components/DotsMenu';
import ItalicSubheader from '@/components/ItalicSubheader';
import ListToggler from '@/components/ListToggler';
import MainLoader from '@/components/MainLoader';

export default {
  name: 'SidebarMeasurementsList',
  components: {
    CardTitleRow,
    DotsMenu,
    ItalicSubheader,
    ListToggler,
    MainLoader,
  },
  props: {
    errorStraightTitle: {
      type: String,
    },
    errorTranslationPath: {
      type: String,
      default: 'button.scadaConnectionError',
    },
    headerStraightTitle: {
      type: String,
    },
    headerTranslationPath: {
      type: String,
      default: 'sidebar.measurements',
    },
    measurements: {
      type: Array,
      default: () => [],
    },
    wrapping: {
      type: Boolean,
      default: false,
    },
    wrappingElementsNumber: {
      type: Number,
      default: 4,
    },
    loading: {
      type: Boolean,
      default: false,
    },
    buttonsAlign: {
      type: String,
      default: 'right',
    },
  },
  computed: {
    computedMeasurements() {
      return this.measurements.map(measurement => {
        const { value, measurement_unit, dotsMenuItems } = measurement;
        return {
          ...measurement,
          tooltipValue: this.getMeasurementValue(value, measurement_unit),
          fixedValue: this.getMeasurementValue(parseFloat(value?.toFixed(3)) || 0, measurement_unit),
          formattedDateText: `${this.$i18n.t('sidebar.dateOfReading')}: ${this.$dayjs(measurement.timestamp).format(
            'LLL'
          )}`,
          hasDotsMenu: dotsMenuItems?.length > 0,
        };
      });
    },
    errorTitle() {
      return this.errorStraightTitle || this.$i18n.t(this.errorTranslationPath) || '';
    },
    headerTitle() {
      return this.headerStraightTitle || this.$i18n.t(this.headerTranslationPath);
    },
  },
  data: () => ({
    fullListVisible: false,
  }),
  methods: {
    contentButtonHandler(button, measurement) {
      this.$emit(button.name, measurement);
    },
    getListeners(measurement) {
      return Object.keys(this.$listeners).reduce((total, key) => {
        return {
          ...total,
          [key]: () => {
            this.$emit(key, measurement);
          },
        };
      }, {});
    },
    getMeasurementValue(value, measurement_unit) {
      return `${value} ${measurement_unit ? `[${measurement_unit}]` : ''}`;
    },
  },
};
</script>

<style lang="scss" scoped>
.line--16 {
  line-height: 16px;
}
.measurement-element:not(:last-child) {
  @include border(bottom);
}
.measurements-list {
  border-radius: 4px;
  box-shadow: 0px 4px 4px rgba(0, 0, 0, 0.25), 0px -1px 0px rgba(0, 0, 0, 0.15) !important;
}
</style>
